<template>
    <div>
        <template v-if="!ready">
            <sau-loading>
                <template slot="message">Fetching Account Details</template>
            </sau-loading>
        </template>
        <template v-else>
            <div class="flex items-center mb-12">
                <div class="border rounded-full mr-4">
                    <img
                        v-b-tooltip
                        :src="profileWidget.avatar"
                        class="w-20 h-20 rounded-full"
                        title="Image provided by Gravatar based on the email."
                    />
                </div>
                <div class="flex-1">
                    <h5 class="sau-para-m">
                        <template v-if="form.firstname || form.lastname">
                            {{ form.firstname }} {{ form.lastname }}
                        </template>
                        <template v-else>
                            New Contact
                        </template>
                    </h5>
                    {{ form.email }}
                </div>
            </div>

            <div class="-m-4 flex flex-col md:flex-row">
                <div class="m-4 w-full md:w-1/4">
                    <h4>Registration Details</h4>
                    <template v-if="startedWithCompanyNameProvided">
                        <p class="text-sm">
                            Contact us to update the Company Name.
                        </p>
                        <p class="text-sm">
                            <a href="/support" class="text-green">
                                <i class="fas fa-comment"></i>
                                Contact
                            </a>
                        </p>
                    </template>
                    <template v-else>
                        <p class="text-sm">
                            Contact details for your account.
                        </p>
                    </template>
                </div>

                <div class="m-4 w-full md:w-3/4">
                    <div class="border p-8">
                        <div v-if="isNewContact" class="row">
                            <div class="col-lg-12">
                                <!-- First Name -->
                                <div class="form-group">
                                    <label for="email">Email</label>
                                    <input type="text"
                                        name="email"
                                        v-model="form.email"
                                        v-on:input="$v.form.email.$touch"
                                        @keyup="triggerAvatar"
                                        :class="{'is-invalid': $v.form.email.$error, 'is-valid': $v.form.email.$dirty && !$v.form.email.$invalid}"
                                        class="form-control"
                                        novalidate>
                                    <!-- Validations -->
                                    <small v-show="!$v.form.email.$dirty" class="text-muted">* Required</small><!-- default message -->
                                    <small v-show="$v.form.email.$dirty && !$v.form.email.$invalid" class="text-muted">&nbsp;</small><!-- Stop input jumping -->
                                    <small v-show="!$v.form.email.required && $v.form.email.$dirty" class="text-danger">* Required</small><!-- Required -->
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-lg-6">
                                <!-- First Name -->
                                <div class="form-group">
                                    <label for="firstname">First Name</label>
                                    <input type="text"
                                        name="firstname"
                                        v-model="form.firstname"
                                        v-on:input="$v.form.firstname.$touch"
                                        @keyup="updateProfileWidget"
                                        :class="{'is-invalid': $v.form.firstname.$error, 'is-valid': $v.form.firstname.$dirty && !$v.form.firstname.$invalid}"
                                        class="form-control"
                                        novalidate>
                                    <!-- Validations -->
                                    <small v-show="!$v.form.firstname.$dirty" class="text-muted">* Required</small><!-- default message -->
                                    <small v-show="$v.form.firstname.$dirty && !$v.form.firstname.$invalid" class="text-muted">&nbsp;</small><!-- Stop input jumping -->
                                    <small v-show="!$v.form.firstname.required && $v.form.firstname.$dirty" class="text-danger">* Required</small><!-- Required -->
                                </div>
                            </div>
                            <div class="col-lg-6">
                                <!-- Last Name -->
                                <div class="form-group">
                                    <label for="lastname">Last Name</label>
                                    <input type="text"
                                        name="lastname"
                                        v-model="form.lastname"
                                        v-on:input="$v.form.lastname.$touch"
                                        @keyup="updateProfileWidget"
                                        :class="{'is-invalid': $v.form.lastname.$error, 'is-valid': $v.form.lastname.$dirty && !$v.form.lastname.$invalid}"
                                        class="form-control"
                                        novalidate>
                                    <!-- Validations -->
                                    <small v-show="!$v.form.lastname.$dirty" class="text-muted">* Required</small><!-- default message -->
                                    <small v-show="$v.form.lastname.$dirty && !$v.form.lastname.$invalid" class="text-muted">&nbsp;</small><!-- Stop input jumping -->
                                    <small v-show="!$v.form.lastname.required && $v.form.lastname.$dirty" class="text-danger">* Required</small><!-- Required -->
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-lg-6">
                                <!-- phonenumber -->
                                <div class="form-group">
                                    <label for="phonenumber">Phone Number</label>
                                    <vue-tel-input
                                        v-model="form.phonenumber"
                                        v-on:input="$v.form.phonenumber.$touch"
                                        :preferredCountries="['AU', 'US', 'GB', 'NZ']"
                                        :mode="'international'"
                                        />
                                    <!-- Validations -->
                                    <small v-show="!$v.form.phonenumber.$dirty" class="text-muted">&nbsp;</small><!-- default message -->
                                    <small v-show="$v.form.phonenumber.$dirty && !$v.form.phonenumber.$invalid" class="text-muted">&nbsp;</small><!-- Stop input jumping -->
                                </div>
                            </div>
                            <div class="col-lg-6">
                                <!-- companyname Name -->
                                <div class="form-group">
                                    <label for="companyname">Company Name</label>
                                    <input type="text"
                                        placeholder=""
                                        name="companyname"
                                        class="form-control"
                                        v-model="form.companyname"
                                        v-on:input="$v.form.companyname.$touch"
                                        :disabled="startedWithCompanyNameProvided"
                                        :class="{'is-invalid': $v.form.companyname.$error, 'is-valid': $v.form.companyname.$dirty && !$v.form.companyname.$invalid}"
                                    />
                                    <!-- Validations -->
                                    <small v-show="!$v.form.companyname.$dirty" class="text-muted">&nbsp;</small><!-- default message -->
                                    <small v-show="$v.form.companyname.$dirty && !$v.form.companyname.$invalid" class="text-muted">&nbsp;</small><!-- Stop input jumping -->
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="-m-4 pt-4 flex flex-col md:flex-row">
                <div class="m-4 w-full md:w-1/4">
                    <h4>Address Details</h4>
                    <p class="text-sm">
                        The billing address information is needed for invoicing.
                    </p>
                    <p class="text-sm">
                        <button
                            type="button"
                            class="text-green hover:underline"
                            @click="useAddressFromMainAccount()"
                        >
                            Copy address from the main account.
                        </button>
                    </p>
                </div>

                <div class="m-4 w-full md:w-3/4">
                    <div class="border p-8">
                        <div class="row">
                            <div class="col">
                                <!-- address 1 -->
                                <div class="form-group">
                                    <label for="address1">Address 1</label>
                                    <input type="text"
                                        name="address1"
                                        v-model="form.address1"
                                        v-on:input="$v.form.address1.$touch"
                                        :class="{'is-invalid': $v.form.address1.$error, 'is-valid': $v.form.address1.$dirty && !$v.form.address1.$invalid}"
                                        class="form-control"
                                        placeholder="">
                                    <!-- Validations -->
                                    <small v-show="!$v.form.address1.$dirty" class="text-muted">&nbsp;</small><!-- default message -->
                                    <small v-show="$v.form.address1.$dirty && !$v.form.address1.$invalid" class="text-muted">&nbsp;</small><!-- Stop input jumping -->
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col">
                                <!-- address 1 -->
                                <div class="form-group">
                                    <label for="address2">Address 2</label>
                                    <input type="text"
                                        name="address2"
                                        v-model="form.address2"
                                        v-on:input="$v.form.address2.$touch"
                                        :class="{'is-invalid': $v.form.address2.$error, 'is-valid': $v.form.address2.$dirty && !$v.form.address2.$invalid}"
                                        class="form-control"
                                        placeholder="">
                                    <!-- Validations -->
                                    <small v-show="!$v.form.address2.$dirty" class="text-muted">&nbsp;</small><!-- default message -->
                                    <small v-show="$v.form.address2.$dirty && !$v.form.address2.$invalid" class="text-muted">&nbsp;</small><!-- Stop input jumping -->
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-lg-9">
                                <!-- City -->
                                <div class="form-group">
                                    <label for="city">City</label>
                                    <input type="text"
                                        name="city"
                                        v-model="form.city"
                                        v-on:input="$v.form.city.$touch"
                                        :class="{'is-invalid': $v.form.city.$error, 'is-valid': $v.form.city.$dirty && !$v.form.city.$invalid}"
                                        class="form-control"
                                        placeholder="">
                                    <!-- Validations -->
                                    <small v-show="!$v.form.city.$dirty" class="text-muted">&nbsp;</small><!-- default message -->
                                    <small v-show="$v.form.city.$dirty && !$v.form.city.$invalid" class="text-muted">&nbsp;</small><!-- Stop input jumping -->
                                </div>
                            </div>
                            <div class="col-lg-3">
                                <!-- Postcode -->
                                <div class="form-group">
                                    <label for="postcode">Postcode</label>
                                    <input type="text"
                                        name="postcode"
                                        v-model="form.postcode"
                                        v-on:input="$v.form.postcode.$touch"
                                        :class="{'is-invalid': $v.form.postcode.$error, 'is-valid': $v.form.postcode.$dirty && !$v.form.postcode.$invalid}"
                                        class="form-control"
                                        placeholder="">
                                    <!-- Validations -->
                                    <small v-show="!$v.form.postcode.$dirty" class="text-muted">&nbsp;</small><!-- default message -->
                                    <small v-show="$v.form.postcode.$dirty && !$v.form.postcode.$invalid" class="text-muted">&nbsp;</small><!-- Stop input jumping -->
                                </div>
                            </div>
                        </div>

                        <div class="row">

                            <div class="col-lg-6">
                                <div class="form-group">
                                    <label for="country">Country</label>
                                    <country-select
                                        id="country"
                                        class="form-control"
                                        v-model="form.country"
                                        :country="form.country"
                                        topCountry="AU"
                                        :class="{'is-invalid': $v.form.country.$error, 'is-valid': $v.form.country.$dirty && !$v.form.country.$invalid}"
                                    />
                                    <small v-show="!$v.form.country.$dirty" class="text-muted">&nbsp;</small>
                                    <small v-show="$v.form.country.$dirty && !$v.form.country.$invalid" class="text-muted">&nbsp;</small>
                                </div>
                            </div>

                            <div class="col-lg-6">
                                <div class="form-group">
                                    <label for="state">State/Region</label>
                                    <region-select
                                        id="state"
                                        class="form-control"
                                        v-model="form.state"
                                        :country="form.country"
                                        :region="form.state"
                                        :regionName="true"
                                        :class="{'is-invalid': $v.form.state.$error, 'is-valid': $v.form.state.$dirty && !$v.form.state.$invalid}"
                                        :placeholder="'Select State/Region'"
                                        :disabled="! form.country"
                                    />
                                    <small v-show="!$v.form.state.$dirty" class="text-muted">&nbsp;</small><!-- default message -->
                                    <small v-show="$v.form.state.$dirty && !$v.form.state.$invalid" class="text-muted">&nbsp;</small><!-- Stop input jumping -->
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            <div class="-m-4 pt-4 flex flex-col md:flex-row">
                <div class="m-4 w-full md:w-1/4">
                    <h4>Permissions and Notifications</h4>
                    <p class="text-sm">
                        Set permissions and email notifications for this contact's account.
                    </p>
                </div>
                <div class="m-4 w-full md:w-3/4">
                    <sau-permissions
                        :settings="{permissions: formPermissions, subscriptions: formSubscriptions, loginAccess: form.subaccount}"
                        :invite-pending="invitePending"
                        :invite-permissions="invitePermissions"
                        v-on:updated="processPermissions" />
                </div>
            </div>

            <div class="-m-4 pt-4 flex flex-col md:flex-row">
                <div class="hidden m-4 w-full md:block md:w-1/4">
                    &nbsp;
                </div>

                <div class="m-4 w-full md:w-3/4 flex items-center" :class="{
                    'justify-end': ! contact,
                    'justify-between': !! contact,
                }">
                    <button
                        type="button"
                        v-if="contact"
                        :disabled="deleting"
                        @click="deleteContact(contact.id, form.firstname, form.lastname)"
                        class="btn text-red hover:text-red-600 no-underline"
                    >
                        Delete Contact
                    </button>
                    <div class="items-center">
                        <button
                            type="button"
                            @click="cancel()"
                            class="btn text-blue hover:text-blue-600 no-underline"
                        >
                            Cancel
                        </button>
                        <button @click.prevent="processContact()" class="btn" :class="{'btn-light': !readyToSubmit, 'btn-success': readyToSubmit}" :disabled="!readyToSubmit">
                            {{ contact ? 'Update Contact' : 'Create Contact' }}
                            <i v-if="processing" class="far fa-spinner-third fa-spin"></i>
                        </button>
                    </div>
                </div>
            </div>




            <!-- Backend errors -->
            <div class="row pt-4" v-show="$v.form.$error">
                <div class="col">
                    <i class="fal fa-exclamation-circle sau-font-color red"></i><span class="sau-font-color red sau-font sm"> Please check all form fields are valid before being able to submit this form</span>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { required, email } from 'vuelidate/lib/validators';
import vSelect from 'vue-select';
import sauUpdateEmail from './_UpdateEmail';
//import changePassword from './_ChangePassword';

import SauPermissions from '@v2/account/partials/Permissions';
import { RegionSelect, CountrySelect } from 'vue-country-region-select';
import { VueTelInput } from 'vue-tel-input';


export default {
    components: { vSelect, sauUpdateEmail, CountrySelect, RegionSelect, VueTelInput, SauPermissions },
    props: [
        'contact'
    ],
    created(){

        if (this.contact) {
            this.populateForm(this.contact);
            this.contactAvatar = this.contact.avatar
            this.isNewContact = false;
        } else {
            this.isNewContact = true;
            this.contactAvatar = '/img/avatar.svg';
        }
        this.updateProfileWidget();

        this.startedWithCompanyNameProvided = !! this.form.companyname;
    },
    data() {
        return {
            ready: true,
            popup: 'For security reasons, only the contact may update their email address',
            deleting: false,
            isNewContact: true,
            processing: false,
            contactAvatar:'',
            startedWithCompanyNameProvided:false,
            form:{
                firstname:'',
                lastname:'',
                email:'',
                address1: '',
                address2: '',
                formattedAddress: '',
                phonenumber: '',
                companyname: '',
                city: '',
                state: '',
                postcode: '',
                country: '',
                subaccount: false,
                generalemails: '',
                productemails: '',
                domainemails: '',
                invoiceemails: '',
                supportemails: '',
                permissions: ''
            },
            features:{},
            loading: false,
            emailFields: ['general', 'product', 'domain', 'invoice', 'support']
        }
    },
    validations: {
        form:{
            firstname: {required},
            lastname:{required},
            email:{required, email},
            phonenumber:{},
            companyname:{},
            address1:{},
            address2:{},
            formattedAddress:{},
            city:{},
            state:{},
            postcode:{},
            country:{},
        },
    },
    computed: {
        readyToSubmit() {
            return this.processing || this.$v.$invalid ? false : true;
        },
        formPermissions() {
            return this.form.permissions.split(',');
        },
        formSubscriptions() {
            return this.emailFields.filter(field => _.get(this.form, `${field}emails`, 0) == 1);
        },
        invitePending() {
            return this.contact && this.contact.invite_pending == '1';
        },
        invitePermissions() {
            return this.contact && this.contact.invite_permissions ? this.contact.invite_permissions.split(',') : [];
        },
    },
    methods:{
        triggerAvatar(){
            if(!this.$v.form.email.$invalid){
                this.getGravatar({email: this.form.email})
                    .then((response) => {
                        this.contactAvatar = response.data.avatarUrl
                        this.updateProfileWidget()
                    })
                    .catch((error) => {
                        this.contactAvatar = '/img/avatar.svg';
                        this.updateProfileWidget()
                    });
            }else{

                this.contactAvatar = '/img/avatar.svg';
                this.updateProfileWidget()
            }
        },
        deleteContact(contactId, firstname, lastname) {
            this.$swal({
                title: 'Are you sure?',
                text: 'This will remove contact  ' + firstname + ' ' + lastname,
                type: 'error',
                showCancelButton: true,
                confirmButtonText: 'Remove this contact',
                confirmButtonClass: 'btn btn-danger',
                cancelButtonText: 'Cancel',
                cancelButtonClass: 'btn btn-link',
                buttonsStyling: false,
                reverseButtons: true,
                showLoaderOnConfirm: true,
                preConfirm: () => {
                    this.deleting = true;
                    return new Promise((resolve) => {
                        setTimeout(() => {
                            this.removeContact(contactId)
                            .then(response => {
                                if(response.data.data.result === 'success'){
                                    this.$swal({
                                        type:'success',
                                        title: 'Contact removed',
                                        showConfirmButton: false,
                                        timer: 2000
                                    });
                                    this.$emit('change',{action: 'delete', id: contactId});
                                    this.deleting = false;
                                }else{
                                    this.$swal({
                                        type:'error',
                                        title: 'There seems to be a problem',
                                        html: 'There has been a problem deleting this contact.',
                                        buttonsStyling: false,
                                        confirmButtonText: 'Close',
                                        confirmButtonClass: 'btn btn-danger'
                                    });
                                }
                                // Contact is not removed from parent component, so just reload the page
                                setTimeout(function(){
                                    window.location = '/account/contacts';
                                }, 2000);
                            })
                            .catch(error => {
                                this.$swal({
                                    type:'error',
                                    title: 'There seems to be a problem',
                                    html: 'There has been a problem deleting this contact.',
                                    buttonsStyling: false,
                                    confirmButtonText: 'Close',
                                    confirmButtonClass: 'btn btn-danger'
                                });
                            })
                            resolve()
                        }, 3000)
                    })
                },
            });
        },
        cancel() {
            this.$swal({
                title: 'Are you sure you want to cancel?',
                text: 'You will lose your changes if you cancel.',
                type: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes',
                confirmButtonClass: 'btn btn-danger',
                cancelButtonText: 'No',
                cancelButtonClass: 'btn btn-link',
                buttonsStyling: false,
                reverseButtons: true,
                showLoaderOnConfirm: true,
                preConfirm: () => {
                    window.scrollTo(0, 0);
                    this.$parent.editContact = '';
                    this.$parent.isNewContact = false;
                },
            });
        },
        populateForm(data) {
            for(var property in this.form){
                if(data[property]){
                    this.form[property] = data[property]
                }
            }
            this.form.contactid = this.contact.id;
            this.form.subaccount = this.form.subaccount == '1' ? true : false;
        },
        //build nice readable address in one line
        buildAddress(){
            return this.form.address1+', '+this.form.city+' '+this.form.state+' '+this.form.postcode+', '+this.form.country
        },
        processContact(){
            this.isNewContact ? this.createContact() : this.updateContact()
        },
        createContact(){
            this.processing = true;
            this.createAccountContact(this.form)
            .then((response) => {
                this.processing = false;
                if(response.status === 200){
                    this.$swal({
                        type:'success',
                        title: 'Contact Created',
                        showConfirmButton: false
                    });
                }
                setTimeout(function(){
                    window.location = '/account/contacts';
                }, 2000);
            })
            .catch((error) => {
                this.processing = false;
                var errorMessage = '';
                var errors = error.response.data.errors
                for(var prop in errors){
                    errors[prop].forEach(function(message){
                        if(message){
                            errorMessage += '<div>'+message+'</div>'
                        }
                    })
                }
                this.$swal({
                    type:'error',
                    title: 'There was an error creating contact',
                    html: errorMessage,
                    showConfirmButton: true,
                    confirmButtonClass: 'btn btn-danger',
                    buttonsStyling: false,
                });
            });
        },
        updateContact() {
            this.processing = true;
            this.updateAccountContact('/api/contacts/'+this.contact.id, this.form)
            .then((response) => {
                this.processing = false;

                if(response.data.status === 'success'){
                    this.$swal({
                        type:'success',
                        title: 'Contact Updated',
                        showConfirmButton: false,
                    });
                    setTimeout(function(){
                        window.location = '/account/contacts';
                    }, 2000);
                } else {
                    this.$swal({
                        type:'error',
                        title: 'There was an error updating contact',
                        showConfirmButton: true,
                        confirmButtonClass: 'btn btn-danger',
                        buttonsStyling: false,
                    });
                }
            })
            .catch((error) => {
                this.processing = false;
                var errorMessage = '';
                var errors = error.response.data.errors
                for(var prop in errors){
                    errors[prop].forEach(function(message){
                        if(message){
                            errorMessage += '<div>'+message+'</div>'
                        }
                    })
                }

                this.$swal({
                    type:'error',
                    title: 'There was an error updating contact',
                    html: errorMessage,
                    showConfirmButton: true,
                    confirmButtonClass: 'btn btn-danger',
                    buttonsStyling: false,
                });
            });
        },
        addressDidChange(search){
            //populate form
            this.form.address1 = search.address
            this.form.city = search.city
            this.form.state = search.state
            this.form.postcode = search.postcode
            this.form.country = search.country
            this.form.formattedAddress = this.buildAddress()
        },
        updateUserFeatures(){

            this.saveUserFeatures('/api/user-feature/contact/'+this.contact.id+'/update', {key: 'confirmed-address', value: 'hide'})
            .then((response) => {

            })
            .catch((error) => {
                this.loading = false;
                this.$swal({
                    type:'error',
                    title: 'There was an error updating this contact',
                    showConfirmButton: false,
                    timer: 3000
                });
            });
        },
        updateProfileWidget(){
            this.$store.commit('setProfileWidget', {firstname:this.form.firstname,lastname:this.form.lastname, avatar:this.contactAvatar});
        },
        useAddressFromMainAccount() {
            this.form.address1 = this.accountHolder.address1;
            this.form.address2 = this.accountHolder.address2;
            this.form.city = this.accountHolder.city;
            this.form.state = this.accountHolder.state;
            this.form.postcode = this.accountHolder.postcode;
            this.form.country = this.accountHolder.country;
        },
        processPermissions(data) {
            this.form.permissions = data.permissions.join(',');

            this.emailFields.map(field => {
                this.form[`${field}emails`] = data.subscriptions.includes(field) ? '1' : '0';
            });

            this.form.subaccount = data.loginAccess;
        }
    }
}
</script>
<style>
.fade-enter-active, .fade-leave-active {
    transition: opacity .5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
}
</style>
